if (!customElements.get('parcel-delivery-estimate')) {
  customElements.define(
    'parcel-delivery-estimate',
    class ParcelDeliveryEstimate extends HTMLElement {
      connectedCallback() {
        if (this.listeners) return;
        this.form = this.querySelector('[data-delivery-form]');
        this.input = this.form?.elements.postcode;
        this.result = this.querySelector('[data-delivery-result]');
        this.button = this.querySelector('[data-delivery-check]');
        this.details = this.querySelector('[data-delivery-details]');
        if (!this.form || !this.input || !this.result) return;
        this.strings = JSON.parse(this.querySelector('[data-delivery-strings]').textContent);
        this.variants = JSON.parse(this.querySelector('[data-delivery-variants]').textContent);
        this.listeners = new AbortController();
        const options = { signal: this.listeners.signal };
        this.input.value = this.constructor.postcode || '';
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.check();
        }, options);
        this.input.addEventListener('input', () => {
          this.input.value = this.input.value.replace(/\D/g, '').slice(0, 4);
          this.invalidate();
        }, options);
        this.productArea = this.closest('.shopify-section') || document;
        this.productArea.addEventListener('change', event => {
          if (event.target.name === 'quantity' || event.target.name === 'id') this.refresh();
        }, options);
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
          this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, event => {
            if (String(event.data?.sectionId) === this.dataset.sectionId) {
              this.dataset.selectedVariant = event.data.variant?.id || '';
              this.refresh();
            }
          });
        }
      }

      disconnectedCallback() {
        this.listeners?.abort();
        this.request?.abort();
        clearTimeout(this.refreshTimer);
        this.unsubscribeCart?.();
        this.unsubscribeVariant?.();
        this.listeners = null;
      }

      invalidate() {
        this.request?.abort();
        this.request = null;
        clearTimeout(this.refreshTimer);
        this.input.removeAttribute('aria-invalid');
        this.result.replaceChildren();
        this.busy(false);
      }

      refresh() {
        const checked = this.lastChecked === this.input.value && this.details.open;
        this.invalidate();
        if (checked) this.refreshTimer = setTimeout(() => this.check(), 300);
      }

      busy(active) {
        this.button.disabled = active;
        this.button.textContent = active ? this.strings.checking : this.strings.submit;
        this.result.setAttribute('aria-busy', String(active));
      }

      message(text, error) {
        this.result.replaceChildren();
        const line = document.createElement('p');
        line.className = error ? 'product__delivery-error' : 'product__delivery-note';
        line.textContent = text;
        this.result.append(line);
      }

      province(postcode) {
        const p = Number(postcode);
        if ((p >= 200 && p <= 299) || (p >= 2600 && p <= 2618) || (p >= 2900 && p <= 2920)) return 'Australian Capital Territory';
        if (p >= 800 && p <= 999) return 'Northern Territory';
        if ((p >= 1000 && p <= 2599) || (p >= 2619 && p <= 2899) || (p >= 2921 && p <= 2999)) return 'New South Wales';
        if ((p >= 3000 && p <= 3999) || (p >= 8000 && p <= 8999)) return 'Victoria';
        if ((p >= 4000 && p <= 4999) || (p >= 9000 && p <= 9999)) return 'Queensland';
        if (p >= 5000 && p <= 5999) return 'South Australia';
        if (p >= 6000 && p <= 6999) return 'Western Australia';
        if (p >= 7000 && p <= 7999) return 'Tasmania';
        return null;
      }

      selectedItem() {
        const productForm = document.getElementById(this.dataset.productForm);
        const id = productForm?.querySelector('[name="id"]')?.value || this.dataset.selectedVariant;
        const index = this.variants.ids.findIndex(value => String(value) === String(id));
        const quantityInput = productForm?.querySelector('[name="quantity"]') || this.productArea.querySelector('[name="quantity"]');
        const quantity = Math.max(1, Math.floor(Number(quantityInput?.value) || 1));
        const submit = productForm?.querySelector('[type="submit"]');
        return { quantity, subtotal: Number(this.variants.prices[index] || 0) * quantity, available: index >= 0 && !submit?.disabled };
      }

      itemRate(province, subtotal) {
        if (subtotal >= Number(this.dataset.freeThreshold)) return 0;
        const rate = province === 'New South Wales' ? this.dataset.rateNsw : province === 'Western Australia' ? this.dataset.rateWa : this.dataset.rateOther;
        const amount = Number(rate);
        return Number.isFinite(amount) && amount >= 0 ? amount : null;
      }

      showResult(cents, days, basis, currency) {
        this.result.replaceChildren();
        const row = document.createElement('p');
        row.className = 'product__delivery-price-row';
        const label = document.createElement('span');
        label.textContent = this.strings.standard;
        const price = document.createElement('strong');
        price.textContent = cents === 0 ? this.strings.free : new Intl.NumberFormat('en-AU', { style: 'currency', currency: currency || 'AUD' }).format(cents / 100);
        row.append(label, price);
        const timing = document.createElement('p');
        timing.className = 'product__delivery-time';
        timing.textContent = days ? this.strings.result.replace('[days]', days) : this.strings.fallback;
        const note = document.createElement('p');
        note.className = 'product__delivery-note';
        note.textContent = basis;
        this.result.append(row, timing, note);
      }

      async rates(postcode, province, signal) {
        const root = window.Shopify?.routes?.root || '/';
        const params = new URLSearchParams({ 'shipping_address[zip]': postcode, 'shipping_address[country]': 'Australia', 'shipping_address[province]': province });
        const init = { credentials: 'same-origin', signal, headers: { Accept: 'application/json' } };
        const prepared = await fetch(root + 'cart/prepare_shipping_rates.json?' + params, { ...init, method: 'POST' });
        if (!prepared.ok) throw new Error(prepared.status === 422 ? 'address' : 'rates');
        for (let attempt = 0; attempt < 12; attempt++) {
          if (attempt) await new Promise((resolve, reject) => {
            const done = () => { signal.removeEventListener('abort', cancel); resolve(); };
            const timer = setTimeout(done, 500);
            const cancel = () => { clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')); };
            if (signal.aborted) { cancel(); return; }
            signal.addEventListener('abort', cancel, { once: true });
          });
          const response = await fetch(root + 'cart/async_shipping_rates.json?' + params, init);
          if (!response.ok) throw new Error(response.status === 422 ? 'address' : 'rates');
          const data = await response.json();
          if (data !== null && Array.isArray(data?.shipping_rates)) return data.shipping_rates;
        }
        throw new Error('rates');
      }

      async check() {
        this.invalidate();
        const postcode = this.input.value.trim();
        const province = this.province(postcode);
        if (!/^\d{4}$/.test(postcode) || !province) {
          this.input.setAttribute('aria-invalid', 'true');
          this.message(this.strings.invalid_postcode, true);
          this.input.focus();
          return;
        }
        this.constructor.postcode = postcode;
        this.lastChecked = postcode;
        const request = new AbortController();
        this.request = request;
        const timeout = setTimeout(() => request.abort(), 12000);
        this.busy(true);
        this.message(this.strings.checking);
        try {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(root + 'cart.js', { credentials: 'same-origin', signal: request.signal, headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('rates');
          const cart = await response.json();
          const days = this.getDeliveryWindow(Number(postcode));
          if (Number(cart.item_count) > 0 && cart.items?.some(item => item.requires_shipping)) {
            const rates = await this.rates(postcode, province, request.signal);
            if (request.signal.aborted || !this.isConnected) return;
            const standard = rates.filter(rate => rate.price !== null && String(rate.price).trim() !== '' && Number.isFinite(Number(rate.price)) && !/pickup|collect|express/i.test(String(rate.name)));
            const rate = standard.sort((a, b) => Number(a.price) - Number(b.price))[0];
            if (!rate) { this.message(this.strings.no_rates, true); return; }
            this.showResult(Math.round(Number(rate.price) * 100), days, this.strings.cart_basis, rate.currency || cart.currency);
          } else {
            const item = this.selectedItem();
            if (!item.available) { this.message(this.strings.unavailable_item, true); return; }
            const cents = this.itemRate(province, item.subtotal);
            if (cents === null) throw new Error('rates');
            this.showResult(cents, days, this.strings.item_basis.replace('[quantity]', String(item.quantity)), 'AUD');
          }
        } catch (error) {
          if (this.request !== request || !this.isConnected) return;
          if (error.message === 'address') this.input.setAttribute('aria-invalid', 'true');
          this.message(error.message === 'address' ? this.strings.invalid_postcode : this.strings.unavailable, true);
        } finally {
          clearTimeout(timeout);
          if (this.request === request) this.busy(false);
        }
      }

      getDeliveryWindow(postcode) {
        const regions = [
          { ranges: [[1000, 1920], [2000, 2239], [2555, 2574], [2740, 2786]], days: '5–6' },
          { ranges: [[2250, 2483], [2500, 2551], [2575, 2594], [2621, 2647], [2649, 2714], [2716, 2716], [2720, 2730], [2787, 2880]], days: '5–7' },
          { ranges: [[200, 250], [2600, 2620], [2900, 2914]], days: '4–6' },
          { ranges: [[3000, 3207], [3335, 3341], [3427, 3442], [3750, 3810], [3910, 3920], [3926, 3944], [3975, 3978], [3980, 3981], [8000, 8785]], days: '5–8' },
          { ranges: [[2648, 2648], [2715, 2715], [2717, 2717], [2731, 2739], [3211, 3334], [3342, 3424], [3444, 3749], [3812, 3909], [3921, 3925], [3945, 3971], [3979, 3979], [3984, 3996]], days: '6–9' },
          { ranges: [[2484, 2490], [4000, 4370], [4373, 4381], [4400, 4405], [4500, 4580], [4600, 4610], [4614, 4615], [9016, 9464], [9726, 9726]], days: '6–8' },
          { ranges: [[4371, 4372], [4382, 4390], [4406, 4498], [4581, 4581], [4611, 4613], [4620, 4723]], days: '4–7' },
          { ranges: [[4724, 4870], [4872, 4873], [4877, 4888]], days: '6–9' },
          { ranges: [[4871, 4871], [4890, 4895]], days: '9–11' },
          { ranges: [[4874, 4876]], days: '11–17' },
          { ranges: [[5000, 5171], [5800, 5950]], days: '5–7' },
          { ranges: [[5201, 5734]], days: '5–8' },
          { ranges: [[6000, 6214], [6800, 6997]], days: '7–10' },
          { ranges: [[6215, 6646]], days: '7–11' },
          { ranges: [[6701, 6770]], days: '10–13' },
          { ranges: [[7000, 7254], [7258, 7923]], days: '6–9' },
          { ranges: [[7255, 7257]], days: '7–11' },
          { ranges: [[800, 821], [828, 834]], days: '7–11' },
          { ranges: [[870, 871]], days: '7–12' },
          { ranges: [[822, 822], [835, 862], [872, 875], [880, 881], [885, 909]], days: '14–17' },
        ];

        return regions.find((region) =>
          region.ranges.some(([start, end]) => postcode >= start && postcode <= end)
        )?.days;
      }
    }
  );
}
